import React, { useState, useEffect, useRef } from 'react'
import { Search } from '@mui/icons-material'
import { searchService, SearchResult } from '../../services/searchService'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  formatGender,
  formatMultipleStatuses,
  formatURL,
} from '../fonctions/formatGenderStatusUrl'

interface SearchBarProps {
  placeholder?: string
  onSearch?: (value: string) => void
  className?: string
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, className }) => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState('')
  const [results, setResults] = useState<SearchResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)

  const debouncedSearch = debounce(async (query: string) => {
    if (query.length < 2) {
      setResults(null)
      return
    }

    try {
      setIsLoading(true)
      const searchResults = await searchService.search(query)
      setResults(searchResults)
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setIsLoading(false)
    }
  }, 300)

  useEffect(() => {
    debouncedSearch(searchValue)
    return () => debouncedSearch.cancel()
  }, [searchValue])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(event.target as Node)
      ) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
    setShowResults(true)
  }

  const handleResultClick = (
    type: 'announce' | 'user',
    id: string | number,
    hit: any,
  ) => {
    if (!id) return
    setShowResults(false)
    setSearchValue('')

    if (type === 'user') {
      window.location.href = `/search/user/${id}`
    } else {
      const gender = hit.user?.userProfile?.gender || 'default'
      const status = hit.status || ''
      const title = hit.title || ''

      window.location.href = `/announce/${formatGender(
        gender,
      )}/${formatMultipleStatuses(status)}/${formatURL(title, Number(id))}`
    }
  }

  return (
    <div className='relative max-w-full'>
      <form
        className={`flex items-center w-full ${className}`}
        onSubmit={e => {
          e.preventDefault()
          if (onSearch) onSearch(searchValue)
        }}
      >
        <div className='relative w-full max-w-full'>
          <input
            type='text'
            className='w-full pl-12 pr-4 py-2 xl:py-3 border-none bg-grisclair rounded-lg focus:outline-none text-base'
            placeholder={t('search-placeholder')}
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={() => setShowResults(true)}
          />
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-redpink text-xl' />
        </div>
      </form>

      {showResults && searchValue.length > 1 && (
        <div
          ref={resultsRef}
          className='absolute w-full mt-1 bg-white rounded-lg shadow-2xl shadow-black z-50 max-h-96 overflow-y-auto'
        >
          {isLoading ? (
            <div className='p-4 text-center text-gray-500'>
              {t('searching')}
            </div>
          ) : results ? (
            <div className='divide-y divide-gray-100'>
              {results.announceResult.hits.length > 0 && (
                <div className='p-2' key='announces-section'>
                  <h3 className='text-sm font-semibold text-white rounded-md px-3 py-2 bg-anthracite mb-2'>
                    {t('announces')} ({results.announceResult.nbHits})
                  </h3>
                  <div className='gap-2'>
                    {results.announceResult.hits.map((hit, index) => (
                      <div
                        key={`announce-${hit.id || index}`}
                        className='px-3 py-2 hover:bg-grisclair cursor-pointer rounded w-full'
                        onClick={() =>
                          handleResultClick('announce', hit.id, hit)
                        }
                      >
                        <div
                          className='font-medium text-redpink truncate'
                          dangerouslySetInnerHTML={{ __html: hit.title }}
                        />
                        <div
                          className='text-sm text-gray-600 truncate'
                          dangerouslySetInnerHTML={{ __html: hit.description }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {results.userResult.hits.length > 0 && (
                <div className='p-2' key='users-section'>
                  <h3 className='text-sm font-semibold text-white rounded-md px-3 py-2 bg-anthracite'>
                    {t('users')} ({results.userResult.nbHits})
                  </h3>
                  <div className='gap-2'>
                    {results.userResult.hits.map((hit, index) => {
                      const userId = (hit as any).userId
                      return (
                        <div
                          key={`user-${userId || index}`}
                          className='px-3 py-2 hover:bg-grisclair cursor-pointer rounded w-full'
                          onClick={() =>
                            userId && handleResultClick('user', userId, hit)
                          }
                        >
                          <div
                            className='font-medium text-redpink truncate'
                            dangerouslySetInnerHTML={{ __html: hit.nickname }}
                          />
                          {(hit.firstname || hit.lastname) && (
                            <div
                              className='text-sm text-gray-600 truncate'
                              dangerouslySetInnerHTML={{
                                __html: `${hit.firstname || ''} ${
                                  hit.lastname || ''
                                }`.trim(),
                              }}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {results.announceResult.hits.length === 0 &&
                results.userResult.hits.length === 0 && (
                  <div
                    className='p-4 text-center text-gray-500'
                    key='no-results'
                  >
                    {t('no-results-found')}
                  </div>
                )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default SearchBar
